(() => {
  const statuses = new Set(['processing', 'label_created', 'shipped', 'in_transit', 'out_for_delivery', 'ready_for_pickup', 'delivered', 'delivery_issue', 'cancelled']);
  const progressIndex = { processing: 0, label_created: 0, shipped: 1, in_transit: 1, out_for_delivery: 2, ready_for_pickup: 2, delivered: 3, delivery_issue: 1 };
  const safeTrackingUrl = (value) => {
    if (typeof value !== 'string' || value.length > 2048) return null;
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      return url.href;
    } catch { return null; }
  };
  const calendarDate = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return null;
    const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
    return Number.isNaN(date.valueOf()) || date.toISOString().slice(0, 10) !== value.slice(0, 10) ? null : date;
  };
  const timestamp = (value) => {
    if (typeof value !== 'string') return null;
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? null : date;
  };
  const init = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const strings = JSON.parse(root.querySelector('[data-tracking-messages]').textContent);
    const text = (key, values = {}) => Object.entries(values).reduce((result, [name, value]) => result.replaceAll(`[[${name}]]`, String(value)), strings[key] || '');
    const form = root.querySelector('[data-tracking-form]');
    const orderInput = form.elements.order_number;
    const emailInput = form.elements.email;
    const error = root.querySelector('[data-error]');
    const submit = root.querySelector('[data-submit]');
    const lookup = root.querySelector('[data-lookup]');
    const results = root.querySelector('[data-results]');
    const shipments = root.querySelector('[data-shipments]');
    const announcement = root.querySelector('[data-announcement]');
    const locale = document.documentElement.lang || 'en';
    const format = (date, options) => new Intl.DateTimeFormat(locale, options).format(date);
    let controller = null;
    let cooldownUntil = 0;
    const setText = (node, selector, value) => { node.querySelector(selector).textContent = value; };
    const showError = (message, input) => {
      error.textContent = message;
      error.hidden = false;
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-errormessage', error.id);
        input.focus();
      }
    };
    const clearError = () => {
      error.hidden = true;
      error.textContent = '';
      [orderInput, emailInput].forEach((input) => { input.removeAttribute('aria-invalid'); input.removeAttribute('aria-errormessage'); });
    };
    const renderShipment = (shipment, index, count) => {
      const fragment = root.querySelector('[data-shipment-template]').content.cloneNode(true);
      const card = fragment.querySelector('article');
      const status = statuses.has(shipment.status) ? shipment.status : 'processing';
      card.dataset.status = status;
      const delivered = status === 'delivered';
      const canShowEstimate = !['processing', 'cancelled'].includes(status);
      const date = calendarDate(delivered ? shipment.delivered_at : canShowEstimate ? shipment.estimated_delivery_date || shipment.estimated_delivery_at : null);
      const today = new Date();
      const todayCalendar = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const pastEstimate = !delivered && date && date.toISOString().slice(0, 10) < todayCalendar;
      const staleEstimate = pastEstimate || status === 'delivery_issue';
      if (count > 1) {
        const label = card.querySelector('[data-package-label]');
        label.textContent = text('package_heading', { index: index + 1, count });
        label.hidden = false;
      }
      if (date) {
        setText(card, '[data-delivery-label]', text(delivered ? 'delivered_on' : staleEstimate ? 'last_estimate' : 'estimated_delivery'));
        setText(card, '[data-month]', format(date, { month: 'long', year: date.getUTCFullYear() === today.getFullYear() ? undefined : 'numeric', timeZone: 'UTC' }));
        setText(card, '[data-day]', format(date, { day: 'numeric', timeZone: 'UTC' }));
        setText(card, '[data-weekday]', format(date, { weekday: 'long', timeZone: 'UTC' }));
        card.querySelector('[data-date]').hidden = false;
        card.querySelector('[data-delivery-word]').hidden = true;
        setText(card, '[data-delivery-note]', text(delivered ? 'delivered_note' : staleEstimate ? 'past_estimate_note' : 'estimate_note'));
      } else {
        setText(card, '[data-delivery-label]', text('progress'));
        setText(card, '[data-delivery-word]', text(`${status}_title`));
        setText(card, '[data-delivery-note]', text(`${status}_note`));
      }
      setText(card, '[data-status-label]', text(`status_${status}`));
      const progress = card.querySelector('[data-progress]');
      progress.hidden = status === 'cancelled';
      [...progress.children].forEach((step, stepIndex) => {
        if (stepIndex < progressIndex[status]) step.classList.add('is-complete');
        if (stepIndex === progressIndex[status]) {
          step.classList.add(delivered ? 'is-complete' : 'is-current');
          step.setAttribute('aria-current', 'step');
        }
        if (status === 'ready_for_pickup' && stepIndex === 2) setText(step, '[data-step-text]', text('status_ready_for_pickup'));
      });
      const trackingUrl = safeTrackingUrl(shipment.tracking_url);
      const carrier = typeof shipment.carrier === 'string' ? shipment.carrier.slice(0, 120) : '';
      const trackingNumber = typeof shipment.tracking_number === 'string' ? shipment.tracking_number.slice(0, 150) : '';
      if (carrier || trackingNumber || trackingUrl) {
        card.querySelector('[data-carrier-wrap]').hidden = false;
        setText(card, '[data-carrier]', carrier || text('carrier_fallback'));
        setText(card, '[data-tracking-number]', trackingNumber);
        if (trackingUrl) {
          const link = card.querySelector('[data-carrier-link]');
          link.href = trackingUrl;
          link.hidden = false;
        }
      }
      const events = Array.isArray(shipment.events) ? shipment.events.filter((event) => event && statuses.has(event.status) && timestamp(event.occurred_at)).slice(0, 25) : [];
      events.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
      if (events.length) {
        card.querySelector('[data-history]').hidden = false;
        const list = card.querySelector('[data-events]');
        events.forEach((event) => {
          const item = document.createElement('li');
          const label = document.createElement('p');
          label.textContent = text(`status_${event.status}`);
          const time = document.createElement('time');
          time.dateTime = event.occurred_at;
          time.textContent = format(new Date(event.occurred_at), { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
          item.append(label, time);
          list.append(item);
        });
      }
      return fragment;
    };
    const render = (data) => {
      if (!data || !statuses.has(data.status) || !Array.isArray(data.shipments)) throw new Error('Invalid response');
      const packages = data.shipments.length ? data.shipments.filter((shipment) => shipment && statuses.has(shipment.status)).slice(0, 30) : [{ status: data.status }];
      if (!packages.length) throw new Error('Invalid shipments');
      shipments.replaceChildren(...packages.map((shipment, index) => renderShipment(shipment, index, packages.length)));
      const notice = root.querySelector('[data-order-notice]');
      const noticeKey = data.status === 'cancelled' ? 'cancelled_notice' : data.partially_fulfilled ? 'partial_notice' : '';
      notice.hidden = !noticeKey;
      notice.textContent = noticeKey ? text(noticeKey) : '';
      setText(root, '[data-order-heading]', text('order_heading', { order: typeof data.order_number === 'string' || typeof data.order_number === 'number' ? String(data.order_number).slice(0, 40) : orderInput.value.trim() }));
      const updated = timestamp(data.updated_at);
      const updatedNode = root.querySelector('[data-updated]');
      updatedNode.hidden = !updated;
      if (updated) updatedNode.textContent = text('updated', { date: format(updated, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) });
      lookup.hidden = true;
      results.hidden = false;
      emailInput.value = '';
      announcement.textContent = text('results_found');
      shipments.querySelector('.fms-track__delivery').focus({ preventScroll: true });
      root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    };
    form.addEventListener('input', clearError);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (controller) return;
      clearError();
      const order = orderInput.value.trim();
      const email = emailInput.value.trim();
      if (!order || order.length > 32) { showError(text('validation_order'), orderInput); return; }
      emailInput.value = email;
      if (!email || !emailInput.validity.valid) { showError(text('validation_email'), emailInput); return; }
      if (Date.now() < cooldownUntil) { showError(text('rate_limited')); return; }
      controller = new AbortController();
      const timer = window.setTimeout(() => controller?.abort(), 20000);
      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      setText(root, '[data-submit-label]', text('loading'));
      announcement.textContent = text('loading');
      try {
        const endpoint = new URL(root.dataset.endpoint);
        if (endpoint.protocol !== 'https:' || endpoint.hostname !== 'fabricmegastore.info') throw new Error('Invalid endpoint');
        const response = await fetch(endpoint.href, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ order_number: order, email }), signal: controller.signal,
          credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer'
        });
        if (response.status === 404 || response.status === 422) { showError(text('not_found')); return; }
        if (response.status === 429) {
          const retrySeconds = Number(response.headers.get('Retry-After'));
          cooldownUntil = Date.now() + Math.min(3600, Math.max(30, Number.isFinite(retrySeconds) ? retrySeconds : 60)) * 1000;
          showError(text('rate_limited')); return;
        }
        if (!response.ok) throw new Error('Unavailable');
        render(await response.json());
      } catch {
        showError(text('unavailable'));
      } finally {
        window.clearTimeout(timer);
        controller = null;
        submit.disabled = false;
        form.removeAttribute('aria-busy');
        setText(root, '[data-submit-label]', text('track_order'));
        if (!error.hidden) announcement.textContent = '';
      }
    });
    root.querySelector('[data-reset]').addEventListener('click', () => {
      results.hidden = true;
      lookup.hidden = false;
      shipments.replaceChildren();
      announcement.textContent = '';
      clearError();
      orderInput.focus();
    });
    const query = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const prefillValue = (value, limit) => {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      return trimmed.length <= limit ? trimmed : '';
    };
    const suppliedOrder = [fragment.get('order'), fragment.get('order_number'), query.get('order'), query.get('order_number')]
      .map((value) => prefillValue(value, 32)).find(Boolean);
    const suppliedEmail = prefillValue(fragment.get('email'), 254);
    if (suppliedOrder) orderInput.value = suppliedOrder;
    if (suppliedEmail) emailInput.value = suppliedEmail;
    const trackingKeys = ['order', 'order_number', 'email'];
    if (trackingKeys.some((key) => fragment.has(key))) {
      trackingKeys.forEach((key) => fragment.delete(key));
      const remainingFragment = fragment.toString();
      try {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${remainingFragment ? `#${remainingFragment}` : ''}`);
      } catch {}
    }
  };
  const initAll = () => document.querySelectorAll('[data-fms-tracking]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
